// Transferir un prospecto a otra asesora activa. Lo puede hacer cualquier
// asesora activa, con un motivo.
import React from 'react'
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react'

import xhr from 'xhr'

const REASONS = [
  { value: 'hibot', label: 'Ya lo atiende otra asesora por HiBot' },
  { value: 'otro', label: 'Otro' },
]

const adviserName = (adviser) =>
  adviser?.user ? `${adviser.user.first_name || ''} ${adviser.user.last_name || ''}`.trim() : ''

export default function TransferProspectModal({ prospect, isOpen, onClose, onTransferred }) {
  const toast = useToast()
  const [advisers, setAdvisers] = React.useState([])
  const [target, setTarget] = React.useState('')
  const [reason, setReason] = React.useState('hibot')
  const [detail, setDetail] = React.useState('')
  const [submitted, setSubmitted] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  const currentAdviserId = prospect?.adviser?.id

  React.useEffect(() => {
    if (!isOpen) return
    setTarget('')
    setReason('hibot')
    setDetail('')
    setSubmitted(false)
    xhr(`/advisers?filters[active][$eq]=true&populate=user&pagination[pageSize]=100`)
      .then((res) => {
        const list = (res.data.data || [])
          .filter((el) => el.user && el.id !== currentAdviserId)
          .sort((a, b) => adviserName(a).localeCompare(adviserName(b), 'es'))
        setAdvisers(list)
      })
      .catch((error) => {
        console.error(error)
        setAdvisers([])
      })
  }, [isOpen, currentAdviserId])

  const missingTarget = !target
  const missingDetail = reason === 'otro' && !detail.trim()

  const handleSubmit = async () => {
    setSubmitted(true)
    if (missingTarget || missingDetail) return
    try {
      setSaving(true)
      const res = await xhr.post(`/prospects/${prospect.id}/transfer`, {
        data: { adviser: Number(target), reason, reason_detail: detail.trim() },
      })
      toast({
        title: `Prospecto transferido a ${res.data?.adviser?.name || 'la asesora elegida'}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      onTransferred?.(res.data)
      onClose()
    } catch (error) {
      console.error(error)
      toast({
        title: error?.response?.data?.error?.message || 'No se pudo transferir el prospecto.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Transferir prospecto</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Text fontSize="sm" color="gray.600">
              <b>{prospect?.name}</b> pasará al escritorio de la asesora que elijas, en estado «Transferido».
              {adviserName(prospect?.adviser) && ` Hoy lo tiene ${adviserName(prospect.adviser)}.`}
            </Text>

            <FormControl isRequired isInvalid={submitted && missingTarget}>
              <FormLabel fontSize="sm">Asesora que lo recibe</FormLabel>
              <Select placeholder="Elige una asesora" value={target} onChange={(e) => setTarget(e.target.value)}>
                {advisers.map((el) => (
                  <option key={el.id} value={el.id}>
                    {adviserName(el)}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>Elige la asesora que recibe el prospecto.</FormErrorMessage>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Motivo</FormLabel>
              <RadioGroup value={reason} onChange={setReason} colorScheme="purple">
                <Stack spacing={2}>
                  {REASONS.map((el) => (
                    <Radio key={el.value} value={el.value}>
                      <Text fontSize="sm">{el.label}</Text>
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>

            <FormControl isRequired={reason === 'otro'} isInvalid={submitted && missingDetail}>
              <FormLabel fontSize="sm">{reason === 'otro' ? '¿Cuál?' : 'Detalle (opcional)'}</FormLabel>
              <Textarea
                rows={2}
                maxLength={200}
                placeholder={reason === 'otro' ? 'Escribe el motivo' : 'Ej.: lo tiene en HiBot desde el 02/10'}
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
              />
              <FormErrorMessage>Escribe el motivo de la transferencia.</FormErrorMessage>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button colorScheme="purple" onClick={handleSubmit} isLoading={saving}>
            Transferir
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
